import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type { Alert, AlertDelivery, AlertFilter, AlertMeasure } from "../admin-client";
import { useAlert, useAlerts } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { NotPermitted } from "./packages";
import { Pagination, ShortID, pageOf, usePaging, when } from "./common";
// The SAME severity colours the failure log uses. `statusTone` is not an
// option: it has no case for "critical", so the loudest row on this screen
// would draw in the same grey as an unrecognised value. Severity is neither a
// status nor a health, and this console already had one answer for it.
import { severityTone } from "./failure-log";

/**
 * `/alerts` — what the platform has NOTICED, as opposed to `/failures`, which
 * is what it got wrong.
 *
 * A row here is one CROSSING: a measured value went past a line somebody set,
 * once, at a moment. It is not a state, and it is not a fault. That
 * distinction is why this screen has no acknowledge control and no resolve
 * control, unlike the failure log next to it — resolution is an edge the
 * ENGINE observes when the wallet is funded again, and a button here that
 * closed an alert would be an operator marking a customer as no longer out of
 * money.
 *
 * THE DEFAULT IS THE OPEN SET, because the default question is triage: which
 * of these people is still stuck. The history is a deliberate second ask, and
 * the heading says which one is on screen — a list of resolved crossings under
 * a heading that reads as current would have an operator chasing a customer
 * who topped up on Tuesday.
 *
 * Behind `wallets:read`, the same grant `/wallets` takes and deliberately not
 * a new one. A runout alert discloses exactly what the wallet list does — is
 * this customer funded — at one moment in the past.
 */
export const ALERTS_READ = "wallets:read";

/** The two kinds 0064's CHECK allows. `usage_drift` has no evaluator in this
 *  build; it is offered because the server accepts it as a filter and an
 *  operator seeing an empty answer for it is being told the truth. */
const ALERT_KINDS = ["wallet_runout", "usage_drift"] as const;

const ALERT_SEVERITIES = ["info", "warning", "critical"] as const;

/** How many rows to ask the server for. The route clamps at 200, so this is
 *  the whole page it will give — the paging below is a window onto rows the
 *  browser is already holding, exactly as `/models` pages its catalogue. */
const ALERTS_LIMIT = 200;
const ALERTS_PER_PAGE = 25;

export interface AlertsSearch {
  kind?: string;
  severity?: string;
  state?: string;
}

/** The unfiltered question, spelled out so a link can ask it without each
 *  call site listing the parameters it is not sending. */
export const NO_ALERT_FILTER: AlertsSearch = {
  kind: undefined,
  severity: undefined,
  state: undefined,
};

export function Alerts({ search }: { search: AlertsSearch }) {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, ALERTS_READ) ? (
          <AlertsScreen search={search} />
        ) : (
          <NotPermitted title="Alerts" permission={ALERTS_READ} />
        )
      }
    </RequireOperator>
  );
}

function AlertsScreen({ search }: { search: AlertsSearch }) {
  const navigate = useNavigate();

  // The form's own state, which is what the operator is TYPING; the URL is
  // what has been asked for. Same split the failure filter makes, for the same
  // reason: a select that navigated on change would put every half-built
  // filter in the back button's history.
  const [kind, setKind] = useState(search.kind ?? "");
  const [severity, setSeverity] = useState(search.severity ?? "");
  const [state, setState] = useState(search.state ?? "open");

  // The URL is the authority. The back button and a pasted link both move it
  // without the form knowing, and a form still showing the previous filter is
  // a control that lies about what is on screen.
  useEffect(() => {
    setKind(search.kind ?? "");
    setSeverity(search.severity ?? "");
    setState(search.state ?? "open");
  }, [search.kind, search.severity, search.state]);

  // Which row is open. One at a time, unlike the failure log's set: the panel
  // below is a full-width card rather than an inline row, so a second open
  // alert would push the first off the screen instead of sitting beside it.
  const [openAlert, setOpenAlert] = useState<string | null>(null);

  // Nothing is narrowed here. The three vocabulary values are CLOSED sets the
  // Admin API refuses with a 400 naming what it accepts, and dropping one on
  // the way out would answer a wider question under a heading claiming a
  // filter — which on this screen means reporting that nobody is running out.
  const filter: AlertFilter = {
    kind: search.kind as AlertFilter["kind"],
    severity: search.severity as AlertFilter["severity"],
    state: search.state === "all" ? "all" : undefined,
    limit: ALERTS_LIMIT,
  };
  const alerts = useAlerts(filter);
  const rows = alerts.data?.alerts ?? [];

  // Above the early returns, on the rule `usePaging` documents: a hook called
  // below one runs on some renders and not others.
  const paging = usePaging(ALERTS_PER_PAGE);
  const paged = pageOf(rows, paging);

  const showingHistory = search.state === "all";
  const narrowed = Boolean(search.kind || search.severity || showingHistory);

  const go = (next: AlertsSearch) => {
    setOpenAlert(null);
    void navigate({ to: "/alerts", search: { ...NO_ALERT_FILTER, ...next } });
  };

  return (
    <>
      <PageHeader
        title="Alerts"
        description="What the platform has noticed: one row is one crossing of a threshold, at the moment it happened. Resolution is the engine's — an alert closes when the condition recovers, not when somebody presses a button."
      />

      <Card title="Filter">
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            go({
              kind: kind || undefined,
              severity: severity || undefined,
              // The default is dropped rather than written: `?state=open` and
              // no parameter at all are the same question, and two links that
              // ask it should not look different.
              state: state === "all" ? "all" : undefined,
            });
          }}
        >
          <Field label="Kind" htmlFor="alerts-kind">
            <Select id="alerts-kind" value={kind} onChange={(event) => setKind(event.target.value)}>
              <option value="">Any kind</option>
              {ALERT_KINDS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Severity" htmlFor="alerts-severity">
            <Select
              id="alerts-severity"
              value={severity}
              onChange={(event) => setSeverity(event.target.value)}
            >
              <option value="">Any severity</option>
              {ALERT_SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="State"
            htmlFor="alerts-state"
            hint="Open is the triage list: the crossings that have not recovered."
          >
            {/* No "any" option, because there is no third value: an alert has
                either recovered or it has not, and "all" IS the any. */}
            <Select id="alerts-state" value={state} onChange={(event) => setState(event.target.value)}>
              <option value="open">Open only</option>
              <option value="all">Open and resolved</option>
            </Select>
          </Field>
          <Button type="submit" busy={alerts.isFetching}>
            Apply
          </Button>
        </form>
      </Card>

      <ErrorAlert error={alerts.error} title="Could not load the alerts" />

      <Card title={showingHistory ? "Every crossing" : "Open crossings"}>
        {alerts.isPending ? (
          <Spinner label="Loading the alerts" />
        ) : rows.length === 0 ? (
          // "Nothing has fired" is a real and excellent answer, but it is a
          // claim about the PLATFORM — so it is only made when the filter
          // narrows nothing. Under one, the same empty table says what was
          // asked instead, because an operator reading "nobody is running out"
          // off a `?severity=critical` filter has been told something false.
          <EmptyState
            title={narrowed ? "Nothing matches that filter" : "Nothing has fired"}
          >
            {narrowed
              ? "No alert matches. Widen the filter before reading this as quiet — the state, kind and severity above all narrow the question."
              : "No threshold has been crossed. Every wallet the sweep has measured is above the line its account is held to."}
          </EmptyState>
        ) : (
          <>
            <DataTable<Alert>
              rows={paged.visible}
              rowKey={(row) => row.id}
              empty="No alert matches that filter."
              columns={[
                {
                  key: "severity",
                  header: "Severity",
                  render: (row) => (
                    <>
                      <Badge tone={severityTone(row.severity)}>{row.severity}</Badge>
                      {/* The absence of `resolved_at` IS the state. There is
                          no status column to read instead, so it is stated in
                          words rather than left to a missing badge. */}
                      <div className="esm-muted-text">
                        {row.resolved_at ? "recovered" : "open"}
                      </div>
                    </>
                  ),
                },
                {
                  key: "kind",
                  header: "Kind",
                  render: (row) => (
                    <>
                      <code>{row.kind}</code>
                      <div className="esm-muted-text">{row.subject_type}</div>
                    </>
                  ),
                },
                {
                  key: "observed",
                  header: "Observed",
                  align: "end",
                  render: (row) => <MeasureValue measure={row.observed} />,
                },
                {
                  key: "threshold",
                  header: "Threshold",
                  align: "end",
                  // Beside the observation and never subtracted from it: the
                  // two can be in different units on a future kind, and a
                  // "shortfall" column would be this console doing arithmetic
                  // across units it was told not to assume.
                  render: (row) => <MeasureValue measure={row.threshold} />,
                },
                {
                  key: "subject",
                  header: "Subject",
                  render: (row) =>
                    // A wallet alert links to the wallet, because that is the
                    // page an operator acts on. Any other subject is shown as
                    // the id it is: a link to a screen that does not exist is
                    // worse than a value to paste.
                    row.subject_type === "wallet" ? (
                      <Link to="/wallets/$walletId" params={{ walletId: row.subject_id }}>
                        <ShortID value={row.subject_id} />
                      </Link>
                    ) : (
                      <ShortID value={row.subject_id} />
                    ),
                },
                {
                  key: "account",
                  header: "Billing account",
                  render: (row) => <ShortID value={row.billing_account_id} />,
                },
                { key: "fired", header: "Fired", render: (row) => when(row.created_at) },
                {
                  key: "resolved",
                  header: "Recovered",
                  render: (row) => when(row.resolved_at),
                },
                {
                  key: "open",
                  header: <span className="esm-visually-hidden">Detail</span>,
                  render: (row) => (
                    <button
                      type="button"
                      className="esm-disclose"
                      aria-expanded={openAlert === row.id}
                      onClick={() => setOpenAlert(openAlert === row.id ? null : row.id)}
                    >
                      {openAlert === row.id ? "Hide" : "Open"}
                      <span className="esm-visually-hidden"> what was sent about this alert</span>
                    </button>
                  ),
                },
              ]}
            />
            <Pagination paged={paged} noun="alerts" />
          </>
        )}
      </Card>

      {openAlert ? <AlertDeliveries alertID={openAlert} /> : null}
    </>
  );
}

/**
 * What actually happened to one crossing, down each of the four channels.
 *
 * This card is the reason the detail route exists. "The customer ran out of
 * money in silence" is the incident the alerting spine is built to make
 * answerable, and answering it means seeing that the email was attempted,
 * against what address, and what the vendor said in return — none of which the
 * list can carry without a query per row.
 *
 * `portal`, `admin_api` and `log` are READS marked delivered the moment the
 * row exists; only `email` is a push that can fail. A pending row on one of
 * the other three is therefore a bug rather than a backlog.
 */
function AlertDeliveries({ alertID }: { alertID: string }) {
  const detail = useAlert(alertID);

  if (detail.error) {
    return (
      <Card title="Alert">
        <ErrorAlert error={detail.error} title="Could not load the alert" />
      </Card>
    );
  }
  if (detail.isPending || !detail.data) {
    return (
      <Card title="Alert">
        <Spinner label="Loading the alert" />
      </Card>
    );
  }

  const { alert, deliveries } = detail.data;

  return (
    <Card
      title="What was sent about this alert"
      actions={<Badge tone={severityTone(alert.severity)}>{alert.severity}</Badge>}
    >
      <dl className="esm-detail-list">
        <dt>Alert</dt>
        <dd>
          <code>{alert.id}</code>
        </dd>
        <dt>Observed against the threshold</dt>
        <dd>
          <MeasureValue measure={alert.observed} /> against{" "}
          <MeasureValue measure={alert.threshold} />
        </dd>
        <dt>Fired</dt>
        <dd>{when(alert.created_at)}</dd>
        <dt>Recovered</dt>
        {/* Stated in words. An em dash here would read as "this console did
            not load the date", which is the one reading that sends somebody
            looking for a bug that is not there. */}
        <dd>{alert.resolved_at ? when(alert.resolved_at) : "Still open"}</dd>
      </dl>

      <DataTable<AlertDelivery>
        rows={deliveries}
        rowKey={(row) => row.channel}
        empty="Nothing was recorded against this alert on any channel, which should be impossible."
        columns={[
          { key: "channel", header: "Channel", render: (row) => <code>{row.channel}</code> },
          {
            key: "status",
            header: "Status",
            render: (row) => (
              <Badge
                tone={
                  row.status === "delivered"
                    ? "success"
                    : row.status === "failed"
                      ? "danger"
                      : "warning"
                }
              >
                {row.status}
              </Badge>
            ),
          },
          {
            key: "destination",
            header: "Destination",
            // The customer's address, on the row where it is the destination
            // of a send that happened. It is operator-only support data and it
            // is not an attribute of the measurement, which is why it is here
            // and not on the alert.
            render: (row) =>
              row.destination ? row.destination : <span className="esm-muted-text">—</span>,
          },
          { key: "attempts", header: "Attempts", align: "end", render: (row) => row.attempts },
          {
            key: "error",
            header: "Last error",
            // The PLATFORM's own error string — the same class of thing
            // /failures discloses — rendered as text and never as markup.
            render: (row) =>
              row.last_error ? (
                <span className="esm-failure__message">{row.last_error}</span>
              ) : (
                <span className="esm-muted-text">—</span>
              ),
          },
          { key: "delivered", header: "Delivered", render: (row) => when(row.delivered_at) },
          { key: "next", header: "Next attempt", render: (row) => when(row.next_attempt_at) },
        ]}
      />
    </Card>
  );
}

/**
 * A measure, rendered the only honest way.
 *
 * `formatted` is the server's own rendering and is present exactly when the
 * unit is money. Nothing here divides `value` by a million to make one: a
 * micro is a millionth of a MINOR unit, so that arithmetic is wrong by a
 * factor of a hundred, and it is wrong quietly.
 *
 * Anything else prints the integer WITH its unit. A `bps` or a `days` figure
 * shown as a bare number is a number a reader will assume is dollars.
 */
export function MeasureValue({ measure }: { measure: AlertMeasure }) {
  if (measure.formatted) {
    return (
      <span className="esm-amount">
        {measure.formatted} <span className="esm-muted-text">{measure.currency}</span>
      </span>
    );
  }
  return (
    <span className="esm-amount">
      {measure.value.toLocaleString()} <span className="esm-muted-text">{measure.unit}</span>
    </span>
  );
}
